import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Badge } from './ui/badge';
import { translateMuscle, translateEquipment } from '../lib/muscleTranslations';
import { exerciseName, getExerciseInputMode, formatDuration, formatCardioSetSummary } from '../lib/exerciseDisplay';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Loader2, PlayCircle, ExternalLink,
  BookmarkCheck, Bookmark,
  Dumbbell, Hash, Flame, Trophy, Clock,
} from 'lucide-react';
import client from '../api/client';
import FullScreenPage from './FullScreenPage';
import { formatDate } from '../lib/dateUtils';
import { normalizeExerciseMediaUrl } from '../lib/exerciseMedia';

const SET_TYPE_STYLE = {
  work:    'bg-primary/10 text-primary border-primary/30',
  warmup:  'bg-amber-500/10 text-amber-500 border-amber-500/20',
  failure: 'bg-red-600/10 text-red-600 border-red-600/20',
  drop:    'bg-teal-500/10 text-teal-500 border-teal-500/20',
  backoff: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
};
const SET_TYPE_LABEL = { work: '', warmup: 'W', failure: 'F', drop: 'D', backoff: 'B' };

/**
 * Unified exercise detail — full-screen overlay.
 * Shows GIF, video link, muscle tags, YOUR HISTORY, then INSTRUCTIONS.
 * Used across Social, Workout, Routines, and Exercise Library.
 *
 * Props:
 *   exercise     — exercise object (required)
 *   open         — boolean (required)
 *   onClose      — () => void (required)
 *   showFavorite — boolean (optional) — renders bookmark icon in header
 *   isFavorited  — boolean (optional)
 *   onToggleFav  — (id) => void (optional)
 *   onAdd        — (exercise) => void (optional) — renders "+ Add" button in header
 */
export default function ExerciseDetailModal({
  exercise,
  open,
  onClose,
  showFavorite = false,
  isFavorited = false,
  onToggleFav,
  onAdd,
  historyUserId = null, // whose history to show; defaults to the caller (used by the coach routine editor to show the CLIENT's history)
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const hasInstructions = (exercise?.instructions_list?.length > 0) || !!exercise?.instructions;

  // Always fetch live exercise details so muscle data, instructions, etc. stay current.
  // Pass the catalog exerciseId when the exercise carries one (routine/workout exercises do) —
  // it resolves reliably even when the stored display name doesn't match the catalog naming
  // (e.g. "Barbell Squat" vs "Squat (Barbell)"), which otherwise returned no instructions.
  const catalogExerciseId = exercise?.exerciseId || null;
  const { data: fullData, isLoading: loadingDetails, isError: detailsError } = useQuery({
    queryKey: ['exerciseDetails', catalogExerciseId || exercise?.id || exercise?.name],
    queryFn: () => client.get(`/exercises/details/${encodeURIComponent(exercise.name)}${catalogExerciseId ? `?exercise_id=${encodeURIComponent(catalogExerciseId)}` : ''}`).then(r => r.data),
    enabled: open && !!exercise?.name,
    staleTime: 5 * 60 * 1000,
  });

  // Resolve id: use exercise.id directly, or fall back to fullData.id (covers old
  // social posts whose exercises_detail was stored without an id field)
  const exerciseId = exercise?.id || fullData?.id;

  // Fetch personal history — always pass ?name= so the backend never needs the ID lookup
  const historyName = exercise?.name;
  const { data: history = null, isLoading: loadingHistory, isError: historyError } = useQuery({
    queryKey: ['exerciseHistory', historyName, historyUserId],
    queryFn: () => client.get(`/exercises/_/history?name=${encodeURIComponent(historyName)}${historyUserId ? `&client_id=${encodeURIComponent(historyUserId)}` : ''}`).then(r => r.data),
    enabled: open && !!historyName,
    staleTime: 5 * 60 * 1000,
  });

  if (!exercise) return null;

  // ── Field resolution (exercise prop first, fullData fallback) ──────────
  // Self-heal dead exercisedb.dev URLs (from cached/snapshotted data) → Cloudinary
  const gifUrl = normalizeExerciseMediaUrl(
    exercise.gifUrl || exercise.image_url || fullData?.gifUrl || fullData?.image_url || ''
  );
  // Prefer live DB data (fullData) for muscles so updates are always reflected
  const targetMuscles = fullData?.targetMuscles?.length > 0
    ? fullData.targetMuscles
    : fullData?.primary_muscles?.length > 0
      ? fullData.primary_muscles
      : exercise.targetMuscles?.length > 0
        ? exercise.targetMuscles
        : exercise.primary_muscles || [];
  const secondaryMuscles = fullData?.secondaryMuscles?.length > 0
    ? fullData.secondaryMuscles
    : fullData?.secondary_muscles?.length > 0
      ? fullData.secondary_muscles
      : exercise.secondaryMuscles?.length > 0
        ? exercise.secondaryMuscles
        : exercise.secondary_muscles || [];
  const equipment = exercise.equipment || fullData?.equipment || '';
  // Support both camelCase (Hevy import) and snake_case (legacy) keys
  const videoUrl = exercise.videoUrl || exercise.video_url
    || fullData?.videoUrl || fullData?.video_url || '';
  const instructionsList = exercise.instructions_list?.length > 0
    ? exercise.instructions_list
    : exercise.instructions
      ? [exercise.instructions]
      : fullData?.instructions_list?.length > 0
        ? fullData.instructions_list
        : fullData?.instructions
          ? [fullData.instructions]
          : [];

  const hasTags = exercise.muscle_group || targetMuscles.length > 0
    || secondaryMuscles.length > 0 || equipment || exercise.is_custom;

  // Logged-set display mode (cardio → "30 min · 9 km/h", time-hold → mm:ss, else kg×reps)
  const exForDisplay = { name: exercise.name, equipment };
  const setInputMode = getExerciseInputMode(exForDisplay);

  const headerActions = (
    <div className="flex items-center gap-1">
      {onAdd && (
        <button
          onClick={() => { onAdd(exercise); onClose(); }}
          className="px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/80 text-white font-bold text-xs transition-colors"
        >
          {t('exerciseDetail.add')}
        </button>
      )}
      {showFavorite && (
        <button
          onClick={() => exerciseId && onToggleFav?.(exerciseId)}
          className="p-2 rounded-full hover:bg-muted active:bg-muted transition-colors"
          aria-label={isFavorited ? t('exerciseDetail.removeFromSaved') : t('exerciseDetail.saveExercise')}
        >
          {isFavorited
            ? <BookmarkCheck className="h-5 w-5 text-primary" />
            : <Bookmark className="h-5 w-5 text-muted-foreground" />}
        </button>
      )}
    </div>
  );

  return (
    <FullScreenPage
      open={open}
      onClose={onClose}
      title={(exerciseName(exercise, language) || '').toUpperCase()}
      rightAction={headerActions}
      className={onAdd ? 'z-[320]' : ''}
    >
      {/* Desktop: two columns (media/tags | history/instructions) so everything
          fits one screen without scrolling. Mobile: single column, same order. */}
      <div className="py-4 max-w-5xl mx-auto lg:grid lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-8 lg:items-start space-y-5 lg:space-y-0">

        {/* ── LEFT COLUMN: media, tutorial link, muscle badges ── */}
        <div className="space-y-5 lg:space-y-4">

        {/* ── GIF / Video preview ───────────────────────────────────────────
            Hevy-sourced exercises ship an MP4; render inline via <video>.
            Legacy ExerciseDB entries use a GIF URL in gifUrl — fall back to <img>. */}
        <div className="w-full max-w-[280px] mx-auto aspect-square rounded-2xl overflow-hidden bg-muted border border-border">
          {videoUrl && videoUrl.endsWith('.mp4') ? (
            <video
              src={videoUrl}
              poster={gifUrl || undefined}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-contain"
            />
          ) : gifUrl ? (
            <img
              src={gifUrl}
              alt={exercise.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-muted-foreground opacity-30" />
            </div>
          )}
        </div>

        {/* ── Video tutorial link ───────────────────────────────────────── */}
        <a
          href={videoUrl || `https://www.youtube.com/results?search_query=${encodeURIComponent((exercise.name || '') + ' exercise tutorial form technique')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-border/60 hover:border-primary/50 hover:bg-primary/90 text-sm font-medium text-muted-foreground hover:text-primary transition-all"
        >
          <PlayCircle className="h-4 w-4" />
          {videoUrl ? t('exerciseDetail.watchDemo') : t('exerciseDetail.searchTutorial')}
          <ExternalLink className="h-3 w-3 opacity-60" />
        </a>

        {/* ── Muscle / equipment badges ─────────────────────────────────── */}
        {hasTags && (
          <div className="flex flex-wrap gap-1.5">
            {exercise.muscle_group && targetMuscles.length === 0 && (
              <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                {translateMuscle(exercise.muscle_group, language)}
              </Badge>
            )}
            {targetMuscles.map(m => (
              <Badge key={m} variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/20 capitalize">{translateMuscle(m, language)}</Badge>
            ))}
            {secondaryMuscles.map(m => (
              <Badge key={m} variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/20 capitalize">{translateMuscle(m, language)}</Badge>
            ))}
            {equipment && (
              <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/20">{translateEquipment(equipment, language)}</Badge>
            )}
            {exercise.is_custom && (
              <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/20">{t('exerciseDetail.custom')}</Badge>
            )}
          </div>
        )}

        </div>{/* ── end LEFT COLUMN ── */}

        {/* ── RIGHT COLUMN: your history + instructions ── */}
        <div className="space-y-5 lg:space-y-6 min-w-0">

        {/* ── YOUR HISTORY ─────────────────────────────────────────────── */}
        {!!historyName && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {t('exerciseDetail.yourHistory')}
            </h4>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : historyError ? (
              <p className="text-xs text-muted-foreground text-center py-4">{t('exerciseDetail.couldNotLoadHistory')}</p>
            ) : !history || history.total_times_used === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Dumbbell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('exerciseDetail.notUsedYet')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Stats row */}
                <div className={`grid gap-2 ${history.personal_record ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <div className="p-3 rounded-xl bg-muted/40 border border-border text-center">
                    <Hash className="h-4 w-4 mx-auto mb-1 text-blue-400" />
                    <p className="text-lg font-bold">{history.total_times_used}</p>
                    <p className="text-xs text-muted-foreground">{t('exerciseDetail.timesUsed')}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/40 border border-border text-center">
                    <Flame className="h-4 w-4 mx-auto mb-1 text-orange-400" />
                    <p className="text-lg font-bold">
                      {history.heaviest_weight}
                      <span className="text-xs text-muted-foreground ml-0.5">kg</span>
                    </p>
                    <p className="text-xs text-muted-foreground">{t('exerciseDetail.heaviest')}</p>
                  </div>
                  {history.personal_record && (
                    <div className="p-3 rounded-xl bg-primary/10 border border-primary/30 text-center">
                      <Trophy className="h-4 w-4 mx-auto mb-1 text-primary" />
                      <p className="text-lg font-bold text-primary">
                        {history.personal_record.e1rm}
                        <span className="text-xs text-muted-foreground ml-0.5">kg</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('exerciseDetail.est1rm')} ({history.personal_record.weight}kg × {history.personal_record.reps})
                      </p>
                    </div>
                  )}
                </div>

                {/* Last session */}
                {history.last_session && (
                  <div className="p-3 rounded-xl bg-muted/30 border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{t('exerciseDetail.lastSession')}</span>
                      <span className="text-xs text-foreground/70 ml-auto">
                        {formatDate(history.last_session.date)}
                      </span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {history.last_session.sets?.map((s, i) => {
                        const type = s.set_type || 'work';
                        const style = SET_TYPE_STYLE[type] || SET_TYPE_STYLE.work;
                        const label = SET_TYPE_LABEL[type];
                        return (
                          <div key={`${i}-${s.weight}-${s.reps}`} className={`text-center px-2.5 py-1.5 rounded-lg border min-w-[60px] ${style}`}>
                            {setInputMode === 'cardio' ? (
                              <p className="text-xs font-bold">{label ? `[${label}] ` : ''}{formatCardioSetSummary(s, exForDisplay)}</p>
                            ) : setInputMode === 'time' ? (
                              <p className="text-xs font-bold">{label ? `[${label}] ` : ''}{formatDuration(s.duration_seconds)}</p>
                            ) : (
                              <>
                                <p className="text-xs font-bold">{label ? `[${label}] ` : ''}{s.weight}kg</p>
                                <p className="text-xs opacity-70">{s.reps} {t('exerciseDetail.reps')}</p>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent sessions */}
                {history.recent_sessions?.length > 1 && (
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      {t('exerciseDetail.recentSessions')}
                    </h5>
                    <div className="space-y-1.5">
                      {history.recent_sessions.slice(1).map((s, i) => (
                        <div key={`${i}-${s.date}`} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-muted/20 transition-colors">
                          <span className="text-xs text-muted-foreground w-20 flex-shrink-0">
                            {formatDate(s.date)}
                          </span>
                          <div className="flex gap-1.5 flex-wrap flex-1">
                            {s.sets?.map((st, j) => {
                              const type = st.set_type || 'work';
                              const style = SET_TYPE_STYLE[type] || SET_TYPE_STYLE.work;
                              const label = SET_TYPE_LABEL[type];
                              return (
                                <span key={`${j}-${st.weight}-${st.reps}`} className={`text-xs px-1.5 py-0.5 rounded border ${style}`}>
                                  {label ? `[${label}] ` : ''}
                                  {setInputMode === 'cardio'
                                    ? formatCardioSetSummary(st, exForDisplay)
                                    : setInputMode === 'time'
                                      ? formatDuration(st.duration_seconds)
                                      : `${st.weight}kg x ${st.reps}`}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── INSTRUCTIONS ─────────────────────────────────────────────── */}
        {detailsError ? (
          <p className="text-xs text-muted-foreground text-center py-4">{t('exerciseDetail.couldNotLoadInstructions')}</p>
        ) : loadingDetails ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : instructionsList.length > 0 ? (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t('exerciseDetail.instructions')}
            </h4>
            <ol className="space-y-1.5">
              {instructionsList.map((inst, i) => (
                <li key={i} className="flex gap-2 text-sm text-foreground/80">
                  <span className="text-primary font-bold text-xs mt-0.5 flex-shrink-0">{i + 1}.</span>
                  <span>{inst}</span>
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        </div>{/* ── end RIGHT COLUMN ── */}

      </div>
    </FullScreenPage>
  );
}
